// meetingRooms/styled.js - Enhanced styled components for meeting room cards
import { styled } from '@mui/material/styles';
import { Box, Paper, IconButton, Typography, Chip } from '@mui/material';

// Header container for the top section
export const HeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
}));

// Enhanced Paper component for each meeting room with beautiful styling
export const RoomPaper = styled(Paper)(({ theme, isExpanded }) => ({
  marginBottom: theme.spacing(3),
  overflow: 'hidden',
  borderRadius: theme.spacing(2),
  transition: 'all 0.3s ease-in-out',
  border: '1px solid #E8E2D4',
  backgroundColor: '#FEFCF8',
  '&:hover': {
    boxShadow: '0 8px 32px rgba(212, 165, 116, 0.15)',
    transform: 'translateY(-2px)',
  },
  ...(isExpanded && {
    boxShadow: '0 12px 40px rgba(212, 165, 116, 0.2)',
    border: '2px solid #D4A574',
  }),
}));

// Header box for each room with image and content layout
export const RoomHeaderBox = styled(Box)(({ theme, isExpanded }) => ({
  padding: 0,
  cursor: 'pointer',
  display: 'flex',
  minHeight: 140,
  position: 'relative',
  overflow: 'hidden',
}));

// Container for the room image
export const RoomImageContainer = styled(Box)({
  width: 200,
  minWidth: 200,
  height: 140,
  position: 'relative',
  overflow: 'hidden',
  backgroundColor: '#F5F1E8',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

// Container for room content (title, capacity, etc.)
export const RoomContentContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(3),
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  position: 'relative',
}));

// Room title styling
export const RoomTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '1.5rem',
  color: '#5D4E37',
  marginBottom: theme.spacing(1),
  lineHeight: 1.2,
}));

// Room subtitle/description styling
export const RoomSubtitle = styled(Typography)(({ theme }) => ({
  color: '#8B7355',
  fontSize: '0.9rem',
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
}));

// Capacity chip styling
export const CapacityChip = styled(Chip)(({ theme }) => ({
  backgroundColor: '#F1C40F',
  color: '#5D4E37',
  fontWeight: 600,
  fontSize: '0.85rem',
  height: 32,
  '& .MuiChip-icon': {
    color: '#5D4E37',
  },
}));

// Expand/collapse button styling
export const ExpandButton = styled(Box)(({ theme, isExpanded }) => ({
  position: 'absolute',
  top: theme.spacing(2),
  right: theme.spacing(2),
  width: 40,
  height: 40,
  borderRadius: '50%',
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.3s ease-in-out',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  '&:hover': {
    backgroundColor: '#F1C40F',
    transform: 'scale(1.1)',
  },
  '& svg': {
    color: '#5D4E37',
    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
    transition: 'transform 0.3s ease-in-out',
  },
}));

// Container for services chips
export const ServicesContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(0.5),
  marginTop: theme.spacing(1),
}));

// Individual service chip styling
export const ServiceChip = styled(Chip)(({ theme }) => ({
  backgroundColor: '#E8E2D4',
  color: '#8B7355',
  fontSize: '0.75rem',
  height: 24,
  '& .MuiChip-label': {
    padding: '0 8px',
  },
}));

// Placeholder icon for when no image is available
export const PlaceholderIconStyled = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
  '& svg': {
    fontSize: 48,
    color: '#D4A574',
    opacity: 0.5,
  },
}));

// Image styling for room images
export const RoomImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  transition: 'transform 0.3s ease-in-out',
  '&:hover': {
    transform: 'scale(1.05)',
  },
});

// Chevron icon button with rotation animation (keeping existing one for compatibility)
export const ChevronIconButton = styled(IconButton)(({ theme, isExpanded }) => ({
  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
  transition: 'transform 0.3s ease-in-out',
  color: 'inherit',
}));

// Section header styling for each tab section
export const SectionHeaderBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
}));

// Container for the save button
export const SaveButtonContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(3),
}));

// Missing styled components from workAreas (reused for meetingRooms)
export const StyledContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  maxWidth: '1200px',
  margin: '0 auto',
}));

export const StyledHeader = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  textAlign: 'center',
}));

export const StyledTitle = styled(Typography)(({ theme }) => ({
  fontSize: '2rem',
  fontWeight: 700,
  color: '#5D4E37',
  marginBottom: theme.spacing(2),
}));

export const StyledWorkAreaDetails = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
}));

export const StyledSectionContent = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

export const StyledLoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '200px',
  padding: theme.spacing(4),
}));

export const StyledEmptyState = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(1),
  backgroundColor: '#E3F2FD',
  border: '1px solid #BBDEFB',
  color: '#1976D2',
  textAlign: 'center',
  marginBottom: theme.spacing(2),
}));

// Floating save button components
export const StyledFloatingSaveContainer = styled(Box)(({ theme }) => ({
  position: 'fixed',
  bottom: theme.spacing(3),
  right: theme.spacing(3),
  zIndex: 1000,
}));

export const StyledFloatingSaveWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  alignItems: 'center',
}));

export const StyledFloatingSaveCancelButton = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  backgroundColor: 'white',
  border: '2px solid #D4A574',
  borderRadius: theme.spacing(1),
  color: '#5D4E37',
  cursor: 'pointer',
  fontWeight: 600,
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    backgroundColor: '#F5F1E8',
    transform: 'translateY(-2px)',
  },
}));

export const StyledFloatingSaveButton = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 3),
  backgroundColor: '#F1C40F',
  border: 'none',
  borderRadius: theme.spacing(1),
  color: '#5D4E37',
  cursor: 'pointer',
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    backgroundColor: '#D4A574',
    transform: 'translateY(-2px)',
  },
  '&:disabled': {
    opacity: 0.6,
    cursor: 'not-allowed',
    transform: 'none',
  },
}));

// Add Meeting Room button styling
export const AddMeetingRoomCard = styled(Paper)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  overflow: 'hidden',
  borderRadius: theme.spacing(2),
  border: '2px dashed #D4A574',
  backgroundColor: '#FEFCF8',
  minHeight: 140,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    backgroundColor: '#F5F1E8',
    borderColor: '#B8956A',
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 32px rgba(212, 165, 116, 0.15)',
  },
}));

export const AddMeetingRoomContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(4),
  color: '#8B7355',
  textAlign: 'center',
}));

export const AddMeetingRoomIcon = styled(Box)(({ theme }) => ({
  width: 60,
  height: 60,
  borderRadius: '50%',
  backgroundColor: '#F1C40F',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.3s ease-in-out',
  '& svg': {
    fontSize: 28,
    color: '#5D4E37',
  },
}));

export const AddMeetingRoomText = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '1.1rem',
  color: '#5D4E37',
}));

export const AddMeetingRoomSubtext = styled(Typography)(({ theme }) => ({
  fontSize: '0.9rem',
  color: '#8B7355',
  opacity: 0.8,
}));